import { useState } from "react";

import { RoleGate } from "@/components/RoleGate";
import { useContagem, useLivroEspelho, useUnidades } from "@/hooks/useApi";

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

export function LivroPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "farmaceutico"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const [aba, setAba] = useState<"espelho" | "contagem">("espelho");
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-semibold">Livro de Controlados</h2>
        <p className="text-sm text-ink-muted">
          Portaria 344/98 (ANVISA) — apoio à escrituração. O livro físico segue oficial;
          aqui é o espelho gerado automaticamente (entradas de NF + saídas por dispensação).
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {(["espelho", "contagem"] as const).map((a) => (
          <button
            key={a}
            className={`px-4 py-2 text-sm font-medium ${aba === a ? "border-b-2 border-anarosa text-anarosa" : "text-ink-muted"}`}
            onClick={() => setAba(a)}
          >
            {a === "espelho" ? "Espelho do Livro" : "Contagem (físico × livro)"}
          </button>
        ))}
      </div>

      {aba === "espelho" ? <Espelho /> : <Contagem />}
    </div>
  );
}

function Espelho() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [unidadeId, setUnidadeId] = useState("");
  const { data: unidades } = useUnidades();
  const { data, isLoading } = useLivroEspelho(ano, mes, unidadeId || undefined);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select className="input max-w-[240px]" value={unidadeId} onChange={(e) => setUnidadeId(e.target.value)}>
          <option value="">Todas as unidades (consolidado)</option>
          {(unidades ?? []).filter((u) => u.ativo).map((u) => (
            <option key={u.id} value={u.id}>{u.nome}</option>
          ))}
        </select>
        <select className="input max-w-[120px]" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
          {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <input className="input max-w-[100px]" type="number" value={ano} onChange={(e) => setAno(Number(e.target.value))} />
        <button className="btn-secondary !py-1 text-sm" onClick={() => window.print()}>Imprimir</button>
      </div>
      {!unidadeId ? (
        <p className="text-xs text-amber-700">
          ⚠️ Visão consolidada é uso interno — o livro oficial (Port. 344) é por estabelecimento.
          Escolha a unidade ou baixe o PDF no menu Relatórios.
        </p>
      ) : null}

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {data && data.substancias.length === 0 ? (
        <p className="text-sm text-ink-muted">Sem movimentos de controlados neste mês.</p>
      ) : null}
      {data?.substancias.map((s) => (
        <div key={s.medicamento_id} className="card p-4">
          <div className="flex items-baseline justify-between">
            <b><code className="text-xs">{s.medicamento_codigo}</code> {s.medicamento_descricao}</b>
            <span className="text-xs text-ink-muted">
              saldo anterior {s.saldo_anterior} → <b>final {s.saldo_final}</b>
            </span>
          </div>
          <table className="mt-2 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="py-1">Data</th><th className="py-1">Mov.</th>
                <th className="py-1 text-right">Qtd</th><th className="py-1">Lote</th>
                <th className="py-1">Histórico</th><th className="py-1 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {s.movimentos.map((m, i) => (
                <tr key={i}>
                  <td className="py-1 text-ink-muted">{new Date(m.data).toLocaleDateString("pt-BR")}</td>
                  <td className="py-1">
                    <span className={m.tipo === "entrada" ? "text-green-700" : m.tipo === "saida" ? "text-anarosa" : "text-ink-muted"}>
                      {m.tipo === "entrada" ? "↓ entrada" : m.tipo === "saida" ? "↑ saída" : "ajuste"}
                    </span>
                  </td>
                  <td className="py-1 text-right">{m.quantidade}</td>
                  <td className="py-1 text-xs">{m.numero_lote ?? "—"}</td>
                  <td className="py-1 text-xs text-ink-muted">{m.historico}</td>
                  <td className="py-1 text-right font-medium">{m.saldo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function Contagem() {
  const [unidadeId, setUnidadeId] = useState("");
  const { data: unidades } = useUnidades();
  const { data, isLoading } = useContagem(unidadeId || undefined);
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        Físico distribuído (Armário + Caixas + Enfermagem + Farmácia) × saldo do livro.
        Divergência ≠ 0 precisa de apuração.
      </p>
      <div className="flex items-center gap-3">
        <select className="input max-w-[240px]" value={unidadeId} onChange={(e) => setUnidadeId(e.target.value)}>
          <option value="">Todas as unidades (consolidado)</option>
          {(unidades ?? []).filter((u) => u.ativo).map((u) => (
            <option key={u.id} value={u.id}>{u.nome}</option>
          ))}
        </select>
        <button className="btn-secondary !py-1 text-sm" onClick={() => window.print()}>Imprimir</button>
      </div>
      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {data && data.length === 0 ? (
        <p className="text-sm text-ink-muted">Nenhum medicamento marcado como Portaria 344 no catálogo ainda.</p>
      ) : null}
      {data && data.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2">Medicamento</th>
                <th className="px-3 py-2 text-right">Armário</th>
                <th className="px-3 py-2 text-right">Caixas</th>
                <th className="px-3 py-2 text-right">Enferm.</th>
                <th className="px-3 py-2 text-right">Farmácia</th>
                <th className="px-3 py-2 text-right">Físico</th>
                <th className="px-3 py-2 text-right">Livro</th>
                <th className="px-3 py-2 text-right">Diverg.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((r) => (
                <tr key={r.medicamento_id} className={r.divergencia !== 0 ? "bg-anarosa-50" : ""}>
                  <td className="px-3 py-1.5"><code className="text-xs">{r.medicamento_codigo}</code> {r.medicamento_descricao}</td>
                  <td className="px-3 py-1.5 text-right">{r.armario}</td>
                  <td className="px-3 py-1.5 text-right">{r.caixas}</td>
                  <td className="px-3 py-1.5 text-right">{r.enfermagem}</td>
                  <td className="px-3 py-1.5 text-right">{r.farmacia}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{r.total_fisico}</td>
                  <td className="px-3 py-1.5 text-right">{r.saldo_livro}</td>
                  <td className={`px-3 py-1.5 text-right font-medium ${r.divergencia !== 0 ? "text-anarosa" : "text-ink-muted"}`}>
                    {r.divergencia > 0 ? `+${r.divergencia}` : r.divergencia}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
